import "./MyRestaurants.scss";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import "./MyRestaurants.scss";
import PageTitle from "../../../components/PageTitle/PageTitle";
import { AuthContext } from "../../../contexts/AuthContext";
import { resolveUrl } from "../../../utils/resolveUrl";
import { CiMapPin, CiEdit } from "react-icons/ci";
import AlertModal from "../../../components/AlertModal/AlertModal";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import {
  DynamicFileInput,
  DynamicImagePreview,
  DynamicInput,
  DynamicSelect,
  DynamicTextarea,
} from "../../../components/Inputs/Inputs";
import Button from "../../../components/Button/Button";
import { Modal } from "../../../components/Modal/Modal";
import ImagePlaceholder from "../../../assets/image_placeholder.png";
import DisplayInfo from "../../../components/DisplayInfo/DisplayInfo";

const emptyCreate = {
  name_pt: "",
  name_en: "",
  name_es: "",
  description_pt: "",
  description_en: "",
  description_es: "",
  location: "",
  file: null,
  previewUrl: "",
};

const emptyEdit = {
  id: null,
  name_pt: "",
  name_en: "",
  name_es: "",
  description_pt: "",
  description_en: "",
  description_es: "",
  location: "",
  image_url: "",
  file: null,
  previewUrl: "",
};

export default function MyRestaurants() {
  const {
    axiosAuth,
    accessibleHotels = [],
    currentUser,
  } = useContext(AuthContext);

  const role = currentUser?.role;
  const canEdit = role === "group_admin" || role === "hotel_admin";

  // hotéis disponíveis ao utilizador
  const hotelOptions = useMemo(
    () =>
      (accessibleHotels || []).map((h) => ({
        value: String(h.id),
        label: h.name,
        group_id: h.group_id,
      })),
    [accessibleHotels]
  );

  // hotel selecionado (auto-seleciona o 1º acessível)
  const [selectedHotelId, setSelectedHotelId] = useState("");
  useEffect(() => {
    if (!selectedHotelId && hotelOptions.length > 0) {
      setSelectedHotelId(hotelOptions[0].value);
    } else if (
      selectedHotelId &&
      !hotelOptions.some((h) => h.value === selectedHotelId)
    ) {
      setSelectedHotelId(hotelOptions[0]?.value || "");
    }
  }, [hotelOptions, selectedHotelId]);

  const selectedHotel = useMemo(
    () => hotelOptions.find((h) => h.value === selectedHotelId),
    [hotelOptions, selectedHotelId]
  );

  // dados do restaurante
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);

  // modal de criação
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);

  // modal de edição
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyEdit);
  const [saving, setSaving] = useState(false);

  // erros
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: "",
  });
  const [confirm, setConfirm] = useState({ open: false, id: null });

  const openError = useCallback((title, message) => {
    setErrorModal({ open: true, title, message });
  }, []);

  const closeError = useCallback(() => {
    setErrorModal({ open: false, title: "", message: "" });
  }, []);

  // carregar restaurantes do hotel selecionado
  const fetchRestaurants = useCallback(async () => {
    setRestaurants([]);
    if (!selectedHotelId) return;
    setLoading(true);
    try {
      const { data } = await axiosAuth.get(
        `/restaurants/hotel/${selectedHotelId}`
      );
      setRestaurants(Array.isArray(data) ? data : []);
    } catch (err) {
      // 404 => sem restaurantes
      if (err?.response?.status === 404) {
        setRestaurants([]);
      } else {
        openError(
          "Erro ao carregar restaurantes",
          err?.response?.data?.detail || err.message
        );
      }
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, selectedHotelId, openError]);

  useEffect(() => {
    fetchRestaurants();
  }, [fetchRestaurants]);

  // criar
  const openCreateModal = useCallback(() => {
    if (createForm.previewUrl) URL.revokeObjectURL(createForm.previewUrl);
    setCreateForm({ ...emptyCreate });
    setIsCreateOpen(true);
  }, [createForm.previewUrl]);

  const updateCreate = useCallback((name, value) => {
    setCreateForm((p) => ({ ...p, [name]: value }));
  }, []);

  const handleCreateImage = useCallback(
    (e) => {
      const file = e.target.files?.[0] ?? null;
      if (!file) return;
      if (createForm.previewUrl) URL.revokeObjectURL(createForm.previewUrl);
      setCreateForm((p) => ({
        ...p,
        file,
        previewUrl: URL.createObjectURL(file),
      }));
    },
    [createForm.previewUrl]
  );

  const handleSubmitCreate = useCallback(async () => {
    if (!selectedHotel) {
      openError("Validação", "Selecione um hotel.");
      return;
    }

    const required = [
      "name_pt",
      "name_en",
      "name_es",
      "description_pt",
      "description_en",
      "description_es",
    ];
    for (const f of required) {
      if (!createForm[f]?.trim()) {
        openError("Validação", "Preencha todos os campos obrigatórios.");
        return;
      }
    }

    setSaving(true);
    try {
      const groupId = selectedHotel.group_id;
      if (!groupId) {
        openError(
          "Validação",
          "Não foi possível determinar o grupo deste hotel."
        );
        setSaving(false);
        return;
      }

      const fd = new FormData();
      fd.append("group_id", String(groupId));
      fd.append("hotel_id", String(selectedHotel.value));
      fd.append("name_pt", createForm.name_pt);
      fd.append("name_en", createForm.name_en);
      fd.append("name_es", createForm.name_es);
      fd.append("description_pt", createForm.description_pt);
      fd.append("description_en", createForm.description_en);
      fd.append("description_es", createForm.description_es);
      fd.append("location", createForm.location || "");
      if (createForm.file) fd.append("image", createForm.file);

      await axiosAuth.post("/restaurants/", fd);
      await fetchRestaurants();
      setIsCreateOpen(false);
    } catch (err) {
      openError(
        "Erro ao criar restaurante",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setSaving(false);
      if (createForm.previewUrl) {
        URL.revokeObjectURL(createForm.previewUrl);
        setCreateForm((p) => ({ ...p, previewUrl: "" }));
      }
    }
  }, [createForm, selectedHotel, axiosAuth, fetchRestaurants, openError]);

  // apagar
  const handleDeleteClick = useCallback((id) => {
    setConfirm({ open: true, id });
  }, []);

  const confirmDelete = useCallback(async () => {
    const id = confirm.id;
    if (!id) return;
    try {
      await axiosAuth.delete(`/restaurants/${id}`);
      await fetchRestaurants();
      setIsEditOpen(false);
    } catch (err) {
      openError(
        "Erro ao apagar restaurante",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setConfirm({ open: false, id: null });
    }
  }, [confirm, axiosAuth, fetchRestaurants, openError]);

  // abrir modal edição
  const openEditModal = useCallback(
    (r) => {
      if (editForm.previewUrl) URL.revokeObjectURL(editForm.previewUrl);
      setEditForm({
        id: r.id,
        name_pt: r.name_pt || "",
        name_en: r.name_en || "",
        name_es: r.name_es || "",
        description_pt: r.description_pt || "",
        description_en: r.description_en || "",
        description_es: r.description_es || "",
        location: r.location || "",
        image_url: r.image_url || "",
        file: null,
        previewUrl: "",
      });
      setIsEditOpen(true);
    },
    [editForm.previewUrl]
  );

  const updateEdit = useCallback((name, value) => {
    setEditForm((p) => ({ ...p, [name]: value }));
  });

  const handleEditImage = useCallback(
    (e) => {
      const file = e.target.files?.[0] ?? null;
      if (!file) return;
      if (editForm.previewUrl) URL.revokeObjectURL(editForm.previewUrl);
      setEditForm((p) => ({
        ...p,
        file,
        previewUrl: URL.createObjectURL(file),
      }));
    },
    [editForm.previewUrl]
  );

  // guardar PUT multipart - envia todos os campos + image (se alterada)
  const handleSubmitEdit = useCallback(async () => {
    if (!editForm.id) return;

    // validações mínimas (mantém o comportamento do admin)
    const required = [
      "name_pt",
      "name_en",
      "name_es",
      "description_pt",
      "description_en",
      "description_es",
    ];

    for (const f of required) {
      if (!editForm[f]?.trim()) {
        openError("Validação", "Preencha todos os campos obrigatórios.");
        return;
      }
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name_pt", editForm.name_pt);
      fd.append("name_en", editForm.name_en);
      fd.append("name_es", editForm.name_es);

      fd.append("description_pt", editForm.description_pt);
      fd.append("description_en", editForm.description_en);
      fd.append("description_es", editForm.description_es);

      fd.append("location", editForm.location || "");

      if (editForm.file) fd.append("image", editForm.file);

      await axiosAuth.put(`/restaurants/${editForm.id}`, fd);

      await fetchRestaurants();
      setIsEditOpen(false);
    } catch (err) {
      openError(
        "Erro ao guardar restaurante",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setSaving(false);
      if (editForm.previewUrl) {
        URL.revokeObjectURL(editForm.previewUrl);
        setEditForm((p) => ({ ...p, previewUrl: "" }));
      }
    }
  }, [editForm, axiosAuth, fetchRestaurants, openError]);

  const currentEditPreview =
    editForm.previewUrl ||
    (editForm.image_url ? resolveUrl(editForm.image_url) : "");

  return (
    <div className="my-restaurants">
      {errorModal.open && (
        <AlertModal
          title={errorModal.title || "Ocorreu um erro"}
          variant="danger"
          message={errorModal.message}
          onClose={closeError}
        />
      )}
      {confirm.open && (
        <ConfirmationModal
          title="Apagar restaurante"
          variant="danger"
          secText="Fechar"
          varText="Apagar"
          message="Tem a certeza que deseja apagar este restaurante?"
          onConfirm={confirmDelete}
          onCancel={() => setConfirm({ open: false, id: null })}
        />
      )}
      <PageTitle link="" title="Os meus restaurantes" />

      <div className="filters">
        <DynamicSelect
          label="Hotel"
          placeholder="Escolher um hotel"
          value={selectedHotelId}
          onChange={(e) => setSelectedHotelId(e.target.value)}
          options={hotelOptions}
          disabled={loading || hotelOptions.length === 0}
        />
        {canEdit && selectedHotelId && (
          <div className="right">
            <Button type="button" onClick={openCreateModal} disabled={loading}>
              Novo restaurante
            </Button>
          </div>
        )}
      </div>

      <div className="container">
        {!loading && selectedHotelId && restaurants.length === 0 && (
          <DisplayInfo
            variant="info"
            message="Não há restaurantes adicionados a este hotel."
          />
        )}
        {!loading &&
          restaurants.map((r) => (
            <div key={r.id} className="restaurant-card">
              <div className="image">
                {r.image_url ? (
                  <img src={resolveUrl(r.image_url)} alt={r.name_pt} />
                ) : (
                  <img src={ImagePlaceholder} alt="Image Placeholder" />
                )}
              </div>
              <div className="content">
                <h3>{r.name_pt}</h3>
                <div className="location">
                  <CiMapPin size={14} />
                  <p>{r.location || ""}</p>
                </div>

                {canEdit && (
                  <div className="actions">
                    <div className="button" onClick={() => openEditModal(r)}>
                      <button>Editar</button>
                      <span>
                        <CiEdit size={14} />
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
      </div>
      <Modal
        isVisible={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Editar restaurante"
        saveText="Guardar"
        deleteText="Apagar"
        onSave={handleSubmitEdit}
        onDelete={() => handleDeleteClick(editForm.id)}
        disableActions={saving}
      >
        <div className="row">
          <DynamicImagePreview imageSrc={currentEditPreview} />
        </div>
        <div className="row">
          <DynamicFileInput
            label="Nova imagem:"
            onChange={handleEditImage}
            name="newImage"
          />
        </div>
        <div className="row three-col">
          <DynamicInput
            label="Nome PT"
            name="name_pt"
            required
            value={editForm.name_pt}
            onChange={(_, v) => updateEdit("name_pt", v)}
          />
          <DynamicInput
            label="Nome EN"
            name="name_en"
            required
            value={editForm.name_en}
            onChange={(_, v) => updateEdit("name_en", v)}
          />
          <DynamicInput
            label="Nome ES"
            name="name_es"
            required
            value={editForm.name_es}
            onChange={(_, v) => updateEdit("name_es", v)}
          />
        </div>
        <div className="row">
          <DynamicTextarea
            label="Descrição PT"
            name="description_pt"
            rows={4}
            required
            value={editForm.description_pt}
            onChange={(_, v) => updateEdit("description_pt", v)}
          />
        </div>
        <div className="row">
          <DynamicTextarea
            label="Descrição EN"
            name="description_en"
            rows={4}
            required
            value={editForm.description_en}
            onChange={(_, v) => updateEdit("description_en", v)}
          />
        </div>
        <div className="row">
          <DynamicTextarea
            label="Descrição ES"
            name="description_es"
            rows={4}
            required
            value={editForm.description_es}
            onChange={(_, v) => updateEdit("description_es", v)}
          />
        </div>
        <div className="row">
          <DynamicInput
            label="Localização"
            name="location"
            value={editForm.location}
            onChange={(_, v) => updateEdit("location", v)}
          />
        </div>
      </Modal>

      {/* modal de criação */}
      <Modal
        isVisible={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Novo restaurante"
        saveText="Adicionar"
        onSave={handleSubmitCreate}
        disableActions={saving}
      >
        <div className="row">
          <DynamicImagePreview imageSrc={createForm.previewUrl} />
        </div>
        <div className="row">
          <DynamicFileInput
            label="Imagem:"
            onChange={handleCreateImage}
            name="newImage"
          />
        </div>
        <div className="row three-col">
          <DynamicInput
            label="Nome PT"
            name="name_pt"
            required
            value={createForm.name_pt}
            onChange={(_, v) => updateCreate("name_pt", v)}
          />
          <DynamicInput
            label="Nome EN"
            name="name_en"
            required
            value={createForm.name_en}
            onChange={(_, v) => updateCreate("name_en", v)}
          />
          <DynamicInput
            label="Nome ES"
            name="name_es"
            required
            value={createForm.name_es}
            onChange={(_, v) => updateCreate("name_es", v)}
          />
        </div>
        <div className="row">
          <DynamicTextarea
            label="Descrição PT"
            name="description_pt"
            rows={4}
            required
            value={createForm.description_pt}
            onChange={(_, v) => updateCreate("description_pt", v)}
          />
        </div>
        <div className="row">
          <DynamicTextarea
            label="Descrição EN"
            name="description_en"
            rows={4}
            required
            value={createForm.description_en}
            onChange={(_, v) => updateCreate("description_en", v)}
          />
        </div>
        <div className="row">
          <DynamicTextarea
            label="Descrição ES"
            name="description_es"
            rows={4}
            required
            value={createForm.description_es}
            onChange={(_, v) => updateCreate("description_es", v)}
          />
        </div>
        <div className="row">
          <DynamicInput
            label="Localização"
            name="location"
            value={createForm.location}
            onChange={(_, v) => updateCreate("location", v)}
          />
        </div>
      </Modal>
    </div>
  );
}
